import { useEffect, useState, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSpinner, faCopy, faPen, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../hooks/auth';
import { useClearAppCachesOnMount } from '../../hooks/useClearAppCachesOnMount';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Helmet } from 'react-helmet-async';
import { getAllForms } from '../../functions/getAllForms';
import { getFormByIdItemId } from '../../functions/getFormById';
import { msgItemIdCopied } from '../../utils/messageToast';

const ITEMS_PER_PAGE = 15;

export default function ListForms() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get('search') || '').trim();
  const { authorizationRA, authorizationForce, user } = useAuthContext();
  const ambient = user?.ambient as string;
  const clientId = user?.clientId as string;

  const token = localStorage.getItem("inForce") === "true" ? authorizationForce : authorizationRA;

  useClearAppCachesOnMount();

  const [allForm, setAllForm] = useState<any[]>([]);
  const [countForms, setCountForms] = useState(0);
  const [loadingPage, setLoadingPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState(searchQuery);

  // Refs para evitar chamadas duplicadas
  const hasLoadedRef = useRef(false);
  const lastTokenRef = useRef<string>('');
  const lastAmbientRef = useRef<string>('');
  const lastSearchQueryRef = useRef<string>('');
  const lastPageRef = useRef<number>(0);
  const isLoadingRef = useRef(false);

  const totalPages = useMemo(() => {
    const pagesByCount = Math.max(1, Math.ceil(countForms / ITEMS_PER_PAGE));
    if (currentPage === 1 && allForm.length > 0 && allForm.length < ITEMS_PER_PAGE) {
      return 1;
    }
    return pagesByCount;
  }, [countForms, allForm.length, currentPage]);

  // Ajusta currentPage se necessário
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setSearch(searchQuery);
  }, [searchQuery]);

  // Carrega formulários - lógica unificada para evitar chamadas duplicadas
  useEffect(() => {
    // Verifica se os dados necessários estão disponíveis
    if (!token || !ambient || !clientId) {
      return;
    }

    // Verifica se já carregou com os mesmos parâmetros (evita chamadas duplicadas)
    const tokenChanged = lastTokenRef.current !== token;
    const ambientChanged = lastAmbientRef.current !== ambient;
    const searchChanged = lastSearchQueryRef.current !== searchQuery;
    const pageChanged = lastPageRef.current !== currentPage;

    // Se não mudou nada relevante e já carregou, não recarrega
    if (hasLoadedRef.current && !tokenChanged && !ambientChanged && !searchChanged && !pageChanged) {
      return;
    }

    // Evita múltiplas chamadas simultâneas
    if (isLoadingRef.current) {
      return;
    }

    const fetchData = async () => {
      isLoadingRef.current = true;
      setLoadingPage(true);

      try {
        if (searchQuery) {
          // Busca por itemId específico
          const noopSetSearch = () => { };
          await getFormByIdItemId(clientId, searchQuery, ambient, setAllForm, noopSetSearch, token);
          setCountForms(1);
          setCurrentPage(1);
          lastSearchQueryRef.current = searchQuery;
          lastPageRef.current = 1;
          lastTokenRef.current = token;
          lastAmbientRef.current = ambient;
          hasLoadedRef.current = true;
        } else {
          // Busca todos os formulários
          await getAllForms(
            setLoadingPage,
            token,
            ambient,
            setAllForm,
            currentPage,
            ITEMS_PER_PAGE,
            setCountForms
          );
          lastTokenRef.current = token;
          lastAmbientRef.current = ambient;
          lastSearchQueryRef.current = '';
          lastPageRef.current = currentPage;
          hasLoadedRef.current = true;
        }
      } catch (error) {
        console.error('Erro ao carregar formulários:', error);
      } finally {
        setLoadingPage(false);
        isLoadingRef.current = false;
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, ambient, clientId, searchQuery, currentPage]);

  const handleFormClick = (formId: string) => {
    navigate(`/section/updateForm?clientId=${clientId}&formId=${formId}`, { state: { formId } });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleCopyItemId = (itemId: string): void => {
    navigator.clipboard.writeText(itemId);
    msgItemIdCopied("ItemId copiado para a área de transferência!");
  }

  const handleCopyId = (id: string): void => {
    navigator.clipboard.writeText(id);
    msgItemIdCopied("Id do formulário copiado para a área de transferência!");
  }

  const handleSearchForm = () => {
    const trimmed = search.trim();
    navigate(`/section/listForms?search=${encodeURIComponent(trimmed)}`);
  };

  const handleCreateForm = () => {
    navigate(`/section/createForm?clientId=${clientId}`);
  };

  return (
    <>
      <Helmet>
        <title>Listagem dos Formulários</title>
      </Helmet>

      <div className="page-shell">
        {loadingPage ? (
          <div className="spinnerContainer">
            <FontAwesomeIcon icon={faSpinner} size="3x" spin />
          </div>
        ) : (
          <div className="table-card w-full max-w-full overflow-hidden">
            <div className="table-card-header">
              <div>
                <h2 className="table-card-title">Formulários</h2>
                <p className="table-card-subtitle">Gerencie todos os formulários do sistema</p>
              </div>
              <div className="table-card-actions">
                <button type="button" className="table-action-button" onClick={handleCreateForm}>
                  Novo Formulário
                </button>
              </div>
            </div>

            <div className="list-forms-toolbar">
              <div className="list-forms-search">
                <FontAwesomeIcon icon={faSearch} className="list-forms-search-icon" />
                <input
                  type="text"
                  placeholder="Busque pelo ID, ItemId ou Alias..."
                  className="list-forms-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchForm()}
                />
              </div>
            </div>

            {/* Tabela desktop - visível apenas em telas médias e maiores */}
            <div className="hidden md:block overflow-x-auto">
              <table className="list-forms-table w-full">
                <thead style={{ borderBottom: '1px solid var(--border-color)', borderRadius: '0.5rem 0.5rem 0.5rem 0.5rem', padding: '0.5rem' }}>
                  <tr>
                    <th className="text-center px-2 py-2 align-center">ID</th>
                    <th className="text-center px-2 py-2 align-center">ItemId</th>
                    <th className="text-center px-2 py-2 align-center">Nome</th>
                    <th className="text-center px-2 py-2 align-center hidden lg:table-cell">Criado em</th>
                    <th className="text-center px-2 py-2 align-center hidden lg:table-cell">Atualizado em</th>
                    <th className="text-center px-2 py-2 align-center">Status</th>
                    <th className="text-center px-2 py-2 align-center">Ações</th>
                  </tr>
                </thead>
                <tbody style={{ borderBottom: '1px solid var(--border-color)', borderRadius: '0.5rem 0.5rem 0.5rem 0.5rem' }}>
                  {allForm.map((form: any) => (
                    <tr key={form.id} className="group bg-hover-item-list-forms">
                      <td className="row-item-list-forms">
                        <div className="list-id-pill">
                          <span className="truncate max-w-[160px]" title={form.id}>{form.id}</span>
                          <button className='list-id-copy' type='button' title='Copiar Id do formulário' onClick={() => handleCopyId(form.id)}>
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </td>
                      <td className="row-item-list-forms">
                        <div className="list-id-pill">
                          <span className="truncate max-w-[160px]" title={form.itemId}>{form.itemId}</span>
                          <button className='list-id-copy' type='button' title='Copiar ItemId do formulário' onClick={() => handleCopyItemId(form.itemId)}>
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        </div>
                      </td>
                      <td className="row-item-list-forms">
                        <span className="truncate max-w-[200px] block">{form.name}</span>
                      </td>
                      <td className="row-item-list-forms hidden lg:table-cell" style={{ textAlign: 'center' }}>
                        {format(new Date(form.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </td>
                      <td className="row-item-list-forms hidden lg:table-cell" style={{ textAlign: 'center' }}>
                        {format(new Date(form.updatedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </td>
                      <td className="row-item-list-forms" style={{ textAlign: 'center' }}>
                        <span className={`bg-form-status ${form.status === 'published' ? 'published' : 'not-published'}`}>
                          {form.status === 'published' ? 'Publicado' : 'Não Publicado'}
                        </span>
                      </td>
                      <td className="row-item-list-forms" style={{ textAlign: 'center' }}>
                        <div className="list-actions">
                          <button className='list-action-icon' type="button" title="Editar formulário" aria-label="Editar formulário" onClick={() => { handleFormClick(form.id); }}>
                            <FontAwesomeIcon icon={faPen} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards mobile - visíveis apenas em telas pequenas */}
            <div className="md:hidden space-y-4">
              {allForm.map((form: any) => (
                <div key={form.id} className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm text-gray-600">ID:</span>
                        <span className="text-sm">{form.id}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm text-gray-600">ItemId:</span>
                        <button className='bg-hover-button-copy-id-form' type='button' title='Copiar ItemId' onClick={() => handleCopyItemId(form.itemId)}>
                          <FontAwesomeIcon icon={faCopy} size="sm" />
                        </button>
                        <span className="text-sm truncate flex-1">{form.itemId}</span>
                      </div>
                      <div className="mb-2">
                        <span className="font-semibold text-sm text-gray-600">Nome:</span>
                        <p className="text-sm mt-1">{form.name}</p>
                      </div>
                    </div>
                    <button className='bg-hover-button-open-form ml-2' type="button" title="Editar formulário" onClick={() => { handleFormClick(form.id); }}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-2">
                    <div>
                      <span className="font-semibold">Criado:</span>
                      <p>{format(new Date(form.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                    </div>
                    <div>
                      <span className="font-semibold">Atualizado:</span>
                      <p>{format(new Date(form.updatedAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <span className={`bg-form-status ${form.status === 'published' ? 'published' : 'not-published'}`}>
                      {form.status === 'published' ? 'Publicado' : 'Não Publicado'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {!searchQuery && (
              <div className="list-forms-pagination-wrapper w-full flex items-center justify-between flex-wrap gap-4">
                <p className="list-forms-results">
                  Mostrando <span>{allForm.length}</span> de <span>{countForms}</span> resultados
                </p>
                <nav aria-label="Paginação" className="flex items-center gap-1 sm:gap-3 flex-wrap justify-center sm:justify-end px-2 sm:px-0">
                  <button
                    type="button"
                    title="Anterior"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={handlePreviousPage}
                    className={`px-2 sm:px-3 py-2 rounded-md transition-colors text-sm sm:text-base ${currentPage <= 1 ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-300' : 'bg-gray-700 text-gray-200 hover:bg-gray-600 cursor-pointer'}`}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      aria-current={currentPage === page ? 'page' : undefined}
                      className={`px-2 sm:px-3 py-2 rounded-md transition-colors cursor-pointer text-sm sm:text-base ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    title="Próxima"
                    aria-label="Próxima página"
                    disabled={currentPage >= totalPages}
                    onClick={handleNextPage}
                    className={`px-2 sm:px-3 py-2 rounded-md transition-colors text-sm sm:text-base ${currentPage >= totalPages ? 'opacity-40 cursor-not-allowed bg-gray-700 text-gray-300' : 'bg-gray-700 text-gray-200 hover:bg-gray-600 cursor-pointer'}`}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </nav>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
